import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatAPI, facilityAPI } from '../services/api';
import { Send, Plus, MessageSquare, Building2, ChevronDown, Paperclip, X, FileText, Image } from 'lucide-react';
import { toast } from 'react-toastify';

function Chat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [conversationType, setConversationType] = useState('general');
  const [showFacilityDropdown, setShowFacilityDropdown] = useState(false);
  const [showConvPanel, setShowConvPanel] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    loadFacilities();
    loadConversations();
  }, []);

  useEffect(() => {
    if (conversationId && conversationId !== 'new') {
      loadMessages(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadFacilities = async () => {
    try {
      const res = await facilityAPI.getAll();
      setFacilities(res.data.facilities || []);
    } catch (err) {}
  };

  const loadConversations = async () => {
    try {
      const res = await chatAPI.getConversations();
      setConversations(res.data.conversations || []);
    } catch (err) {}
  };

  const loadMessages = async (id) => {
    try {
      const res = await chatAPI.getMessages(id);
      setMessages(res.data.messages || []);
      const conv = conversations.find(c => c.id === parseInt(id));
      if (conv) setCurrentConversation(conv);
    } catch (err) {
      toast.error('Failed to load messages');
    }
  };

  const deleteConversation = async (e, convId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await chatAPI.deleteConversation(convId);
      toast.success('Conversation deleted');
      if (currentConversation?.id === convId) {
        setCurrentConversation(null);
        setMessages([]);
        navigate('/chat/new');
      }
      loadConversations();
    } catch (err) {
      toast.error('Failed to delete conversation');
    }
  };

  const startNewConversation = async () => {
    try {
      const res = await chatAPI.createConversation({
        facilityId: selectedFacility || null,
        conversationType,
        title: selectedFacility
          ? `${facilities.find(f => f.id === parseInt(selectedFacility))?.name || 'Facility'} Chat`
          : 'General Compliance Chat'
      });
      const conv = res.data.conversation;
      setCurrentConversation(conv);
      setMessages([]);
      await loadConversations();
      navigate(`/chat/${conv.id}`);
      return conv;
    } catch (err) {
      toast.error('Failed to start conversation');
      return null;
    }
  };

  // ── File handling ──────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowed = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.csv', '.txt', '.docx', '.xlsx'];
    const valid = files.filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!allowed.includes(ext)) { toast.warn(`${f.name} — unsupported type. Use PDF, image, CSV, or TXT.`); return false; }
      if (f.size > 20 * 1024 * 1024) { toast.warn(`${f.name} — too large. Max 20MB.`); return false; }
      return true;
    });
    const total = attachedFiles.length + valid.length;
    if (total > 10) { toast.warn('Maximum 10 files per message.'); return; }
    setAttachedFiles(prev => [...prev, ...valid.map(f => ({ file: f, name: f.name, size: f.size }))]);
    e.target.value = '';
  };

  const removeFile = (idx) => setAttachedFiles(prev => prev.filter((_, i) => i !== idx));

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const allowed = ['.pdf','.png','.jpg','.jpeg','.webp','.csv','.txt','.docx','.xlsx'];
    const valid = files.filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!allowed.includes(ext)) { toast.warn(`${f.name} — unsupported. Use PDF, image, CSV, or TXT.`); return false; }
      if (f.size > 20 * 1024 * 1024) { toast.warn(`${f.name} — too large. Max 20MB.`); return false; }
      return true;
    });
    const total = attachedFiles.length + valid.length;
    if (total > 10) { toast.warn('Maximum 10 files per message.'); return; }
    setAttachedFiles(prev => [...prev, ...valid.map(f => ({ file: f, name: f.name, size: f.size }))]);
  };

  const getFileIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (['png','jpg','jpeg','webp'].includes(ext)) return <Image size={12} />;
    return <FileText size={12} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024*1024) return `${(bytes/1024).toFixed(0)}KB`;
    return `${(bytes/1024/1024).toFixed(1)}MB`;
  };

  // ── Send message ────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!newMessage.trim() && attachedFiles.length === 0) return;

    let convId = currentConversation?.id || conversationId;
    if (!convId || convId === 'new') {
      const conv = await startNewConversation();
      if (!conv) return;
      convId = conv.id;
    }

    const userMsg = {
      role: 'user',
      content: newMessage,
      attachments: attachedFiles.map(f => ({ name: f.name, type: f.name.split('.').pop() }))
    };
    setMessages(prev => [...prev, userMsg]);
    const msgText = newMessage;
    const files = [...attachedFiles];
    setNewMessage('');
    setAttachedFiles([]);
    setLoading(true);

    try {
      let res;
      if (files.length > 0) {
        // Multipart form data for file uploads
        const formData = new FormData();
        formData.append('message', msgText);
        files.forEach(f => formData.append('files', f.file));
        // Use axios directly with multipart
        const { default: axios } = await import('axios');
        const token = localStorage.getItem('token');
        const apiRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/chat/conversations/${convId}/messages`,
          formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        res = { data: apiRes.data };
      } else {
        res = await chatAPI.sendMessage(convId, { message: msgText });
      }
      setMessages(prev => [...prev, res.data.message]);
    } catch (err) {
      toast.error('Failed to send message');
      setMessages(prev => prev.slice(0, -1)); // remove optimistic user message
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectedFacilityName = facilities.find(f => f.id === parseInt(selectedFacility))?.name;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)', gap: 20, position: 'relative' }}>

      {showConvPanel && (
        <div onClick={() => setShowConvPanel(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 49, display: 'none' }} className="chat-mobile-overlay" />
      )}

      {/* Left sidebar */}
      <div className={`chat-conv-panel${showConvPanel ? ' open' : ''}`} style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button onClick={() => { navigate('/chat/new'); setCurrentConversation(null); setMessages([]); }} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
          <Plus size={16} /> New Chat
        </button>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {conversations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#6B7280', fontSize: 13 }}>No conversations yet</div>
          ) : (
            conversations.map(conv => (
              <div key={conv.id}
                onClick={() => { navigate(`/chat/${conv.id}`); loadMessages(conv.id); }}
                style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: currentConversation?.id === conv.id ? '#EFF6FF' : 'transparent', border: currentConversation?.id === conv.id ? '1px solid #BFDBFE' : '1px solid transparent', transition: 'all 0.12s', position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 8 }}
                onMouseEnter={e => { if (currentConversation?.id !== conv.id) e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.querySelector('.del-btn').style.opacity = '1'; }}
                onMouseLeave={e => { if (currentConversation?.id !== conv.id) e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.del-btn').style.opacity = '0'; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0A1628', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{conv.title || 'Chat'}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>{conv.facilityName ? `📍 ${conv.facilityName}` : '💬 General'} · {conv.messageCount || 0} msgs</div>
                </div>
                <button className="del-btn" onClick={(e) => deleteConversation(e, conv.id)}
                  style={{ opacity: 0, transition: 'opacity 0.15s', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#DC2626', fontSize: 14, borderRadius: 4, flexShrink: 0, lineHeight: 1 }}>×</button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDragging && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,102,255,0.06)', border: '2px dashed #0066FF', borderRadius: 12, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0066FF' }}>Drop files to attach</div>
              <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)', marginTop: 4 }}>PDF, Word, Excel, image, CSV, TXT — up to 10 files</div>
            </div>
          </div>
        )}

        <button onClick={() => setShowConvPanel(!showConvPanel)} className="chat-conv-toggle btn btn-ghost" style={{ marginBottom: 10, display: 'none', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <MessageSquare size={14} /> Conversations ({conversations.length})
        </button>

        {/* Header */}
        <div className="card" style={{ padding: '14px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MessageSquare size={18} color="#0066FF" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628' }}>AirComply AI Assistant</div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#6B7280', marginTop: 1 }}>
              Ask questions about air quality compliance, permits, and regulations · Attach PDFs, images, or CSV files
            </div>
          </div>
          {conversationId === 'new' && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowFacilityDropdown(!showFacilityDropdown)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: '1px solid #E2E8F0', borderRadius: 8, background: 'white', fontSize: 14, fontFamily: 'var(--font-body)', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <Building2 size={14} color="#6B7280" />
                {selectedFacilityName || 'All facilities'}
                <ChevronDown size={13} color="#6B7280" />
              </button>
              {showFacilityDropdown && (
                <>
                  <div onClick={() => setShowFacilityDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, width: 220, background: 'white', border: '1px solid #E2E8F0', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden' }}>
                    <div onClick={() => { setSelectedFacility(''); setShowFacilityDropdown(false); }}
                      style={{ padding: '9px 14px', fontSize: 14, color: '#374151', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      All facilities (general)
                    </div>
                    {facilities.map(f => (
                      <div key={f.id} onClick={() => { setSelectedFacility(f.id.toString()); setShowFacilityDropdown(false); }}
                        style={{ padding: '9px 14px', fontSize: 14, color: '#374151', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        📍 {f.name || f.facility_name}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 16 }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px' }}>
              <MessageSquare size={48} color="#CBD5E1" style={{ margin: '0 auto 16px', display: 'block' }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0A1628', marginBottom: 6 }}>Ask me anything about air compliance</div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#6B7280', marginBottom: 24 }}>
                Ask regulatory questions, attach permits, stack test reports, CEMS data, or spec sheets.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {['What are my NOx emission limits?', 'When is my annual compliance report due?', 'What is Permit Shield?', 'How do I calculate VOC emissions?'].map(q => (
                  <button key={q} onClick={() => setNewMessage(q)}
                    style={{ padding: '7px 14px', border: '1px solid #E2E8F0', borderRadius: 20, fontSize: 13, fontFamily: 'var(--font-body)', color: '#374151', background: 'white', cursor: 'pointer' }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '75%' }}>
                {/* File attachments on user messages */}
                {msg.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, justifyContent: 'flex-end' }}>
                    {msg.attachments.map((att, ai) => (
                      <div key={ai} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 9px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 20, fontSize: 11, color: '#1E40AF', fontFamily: 'var(--font-body)' }}>
                        <FileText size={10} /> {att.name}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ padding: '11px 15px', borderRadius: msg.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px', background: msg.role === 'user' ? '#0066FF' : 'white', color: msg.role === 'user' ? 'white' : '#0A1628', border: msg.role === 'assistant' ? '1px solid #E2E8F0' : 'none', fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {msg.role === 'assistant' && (
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#0066FF', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>AirComply AI</div>
                  )}
                  {msg.content}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ padding: '11px 16px', borderRadius: '12px 12px 12px 3px', background: 'white', border: '1px solid #E2E8F0' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#CBD5E1', animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite` }} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* File previews */}
        {attachedFiles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {attachedFiles.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 20, fontSize: 12, fontFamily: 'var(--font-body)', color: '#15803D' }}>
                {getFileIcon(f.name)}
                <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <span style={{ color: '#94a3b8', fontSize: 10 }}>({formatFileSize(f.size)})</span>
                <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 0, display: 'flex', alignItems: 'center' }}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* AI disclaimer for file uploads */}
        {attachedFiles.length > 0 && (
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'var(--font-body)', fontStyle: 'italic', marginBottom: 6 }}>
            Files are processed in memory and not stored. They are not retained after this session.
          </div>
        )}

        {/* Input */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          {/* File attach button */}
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt,.docx,.xlsx" style={{ display: 'none' }} onChange={handleFileSelect} />
          <button onClick={() => fileInputRef.current?.click()} disabled={attachedFiles.length >= 10}
            title="Attach files (PDF, Word, Excel, image, CSV, TXT — max 10 files, 20MB each)"
            style={{ padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 10, background: 'white', cursor: attachedFiles.length >= 10 ? 'not-allowed' : 'pointer', color: '#6B7280', display: 'flex', alignItems: 'center', alignSelf: 'stretch', opacity: attachedFiles.length >= 10 ? 0.4 : 1 }}>
            <Paperclip size={16} />
          </button>

          <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={handleKeyDown}
            placeholder={attachedFiles.length > 0 ? 'Add a question about the attached file(s)...' : 'Ask a compliance question... (Enter to send, Shift+Enter for new line)'}
            rows={2}
            style={{ flex: 1, padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 14, fontFamily: 'var(--font-body)', resize: 'none', outline: 'none', lineHeight: 1.5 }}
            onFocus={e => e.target.style.borderColor = '#0066FF'}
            onBlur={e => e.target.style.borderColor = '#E2E8F0'}
          />
          <button onClick={sendMessage} disabled={loading || (!newMessage.trim() && attachedFiles.length === 0)} className="btn btn-primary"
            style={{ padding: '10px 18px', alignSelf: 'stretch', opacity: loading || (!newMessage.trim() && attachedFiles.length === 0) ? 0.5 : 1 }}>
            <Send size={16} />
          </button>
        </div>

        <style>{`
          @keyframes bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }
          @media (max-width: 768px) {
            .chat-conv-panel { position: fixed !important; left: 0; top: 0; bottom: 0; width: 280px !important; background: white; z-index: 50; padding: 20px 12px; box-shadow: 4px 0 24px rgba(0,0,0,0.12); transform: translateX(-100%); transition: transform 0.25s ease; overflow-y: auto; }
            .chat-conv-panel.open { transform: translateX(0); }
            .chat-conv-toggle { display: flex !important; }
            .chat-mobile-overlay { display: block !important; }
          }
        `}</style>
      </div>
    </div>
  );
}

export default Chat;
